import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const positionClasses = {
  none: '',
  top: 'tw-bottom-full tw-left-1/2 -tw-translate-x-1/2 tw-mb-2',
  bottom: 'tw-top-full tw-left-1/2 -tw-translate-x-1/2 tw-mt-2',
  'bottom-start': 'tw-top-full tw-left-0 tw-mt-2',
  'bottom-end': 'tw-top-full tw-right-0 tw-mt-2',
  left: 'tw-right-full tw-top-1/2 -tw-translate-y-1/2 tw-mr-2',
  right: 'tw-left-full tw-top-1/2 -tw-translate-y-1/2 tw-ml-2'
};

const VIEWPORT_MARGIN = 8;

const isVerticalPosition = (pos) => (
  pos === 'top' || pos === 'bottom' || pos === 'bottom-start' || pos === 'bottom-end'
);

const getClippingBounds = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (style.overflowX === 'hidden' || style.overflowX === 'auto' || style.overflowX === 'scroll') {
      const rect = node.getBoundingClientRect();
      return { left: rect.left, right: rect.right };
    }
    node = node.parentElement;
  }
  return { left: 0, right: window.innerWidth };
};

/**
 * @param {string} [props.position='none'] - Position: none | top | bottom | bottom-start | bottom-end | left | right.
 */
const getPortalStyle = (triggerEl, preferredPosition) => {
  const rect = triggerEl.getBoundingClientRect();
  const centerX = rect.left + (rect.width / 2);
  const preferTop = preferredPosition === 'top' || preferredPosition === 'none';
  const spaceAbove = rect.top;
  const useTop = preferTop && spaceAbove > 72;

  return {
    position: 'fixed',
    left: `${centerX}px`,
    top: useTop ? `${rect.top - 8}px` : `${rect.bottom + 8}px`,
    transform: useTop ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
    zIndex: 10050
  };
};

const TooltipMC = ({
  text,
  position = 'none',
  children,
  className = '',
  positionClassName = '',
  truncate = false,
  portal = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [resolvedPosition, setResolvedPosition] = useState(position);
  const [clampStyle, setClampStyle] = useState(null);
  const [hasMeasured, setHasMeasured] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  /** Evita alternar left/right indefinidamente cuando ninguna cabe en el contenedor con overflow. */
  const horizontalAdjustRef = useRef(null);

  const handleMouseEnter = () => {
    if (!text) return;

    horizontalAdjustRef.current = null;
    setResolvedPosition(position);
    setClampStyle(portal && triggerRef.current ? getPortalStyle(triggerRef.current, position) : null);
    setHasMeasured(Boolean(portal));
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    horizontalAdjustRef.current = null;
    setIsVisible(false);
    setResolvedPosition(position);
    setClampStyle(null);
    setHasMeasured(false);
  };

  useLayoutEffect(() => {
    if (!isVisible || !text || !tooltipRef.current) return;
    if (portal) {
      if (triggerRef.current) {
        setClampStyle(getPortalStyle(triggerRef.current, position));
        setHasMeasured(true);
      }
      return;
    }

    const tooltipEl = tooltipRef.current;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const bounds = getClippingBounds(tooltipEl);
    const minLeft = bounds.left + VIEWPORT_MARGIN;
    const maxRight = bounds.right - VIEWPORT_MARGIN;

    if (resolvedPosition === 'right' && tooltipRect.right > maxRight) {
      if (horizontalAdjustRef.current === 'from-left') {
        setResolvedPosition('top');
        horizontalAdjustRef.current = null;
      } else {
        setResolvedPosition('left');
        horizontalAdjustRef.current = 'from-right';
      }
      return;
    }

    if (resolvedPosition === 'left' && tooltipRect.left < minLeft) {
      if (horizontalAdjustRef.current === 'from-right') {
        setResolvedPosition('top');
        horizontalAdjustRef.current = null;
      } else {
        setResolvedPosition('right');
        horizontalAdjustRef.current = 'from-left';
      }
      return;
    }

    if ((resolvedPosition === 'right' || resolvedPosition === 'left')
      && (tooltipRect.right > maxRight || tooltipRect.left < minLeft)) {
      horizontalAdjustRef.current = null;
      setResolvedPosition('top');
      return;
    }

    if (isVerticalPosition(resolvedPosition)
      && (tooltipRect.right > maxRight || tooltipRect.left < minLeft)) {
      const offsetParent = tooltipEl.offsetParent;
      const offsetParentRect = offsetParent?.getBoundingClientRect();
      if (offsetParentRect) {
        let desiredLeftViewport = tooltipRect.left;
        if (tooltipRect.right > maxRight) {
          desiredLeftViewport = maxRight - tooltipRect.width;
        }
        if (desiredLeftViewport < minLeft) {
          desiredLeftViewport = minLeft;
        }
        const offsetLeft = desiredLeftViewport - offsetParentRect.left;
        setClampStyle({
          left: `${offsetLeft}px`,
          right: 'auto',
          transform: 'none'
        });
        horizontalAdjustRef.current = null;
        setHasMeasured(true);
        return;
      }
    }

    horizontalAdjustRef.current = null;
    setHasMeasured(true);
  }, [isVisible, position, portal, resolvedPosition, text]);

  const textClasses = truncate
    ? 'tw-truncate'
    : 'tw-whitespace-pre-line tw-break-words';

  const visibilityClass = hasMeasured ? 'tw-visible tw-opacity-100' : 'tw-invisible tw-opacity-0';

  const tooltipNode = isVisible && text ? (
    <div
      ref={tooltipRef}
      style={clampStyle || undefined}
      className={`${portal ? 'tw-fixed' : `tw-absolute ${positionClasses[resolvedPosition]}`} tw-bg-gray-950 tw-text-gray-200 tw-text-[13px] tw-rounded-[6px]
                     tw-px-[10px] tw-py-[6px] tw-shadow-md tw-w-max tw-max-w-xs tw-z-[10050] tw-pointer-events-none ${textClasses} ${visibilityClass} ${positionClassName}`}
    >
      {text}
    </div>
  ) : null;

  return (
    <div className={`tw-wrapper tw-relative tw-inline-block ${className}`}>
      <div
        ref={triggerRef}
        className="tw-h-full tw-w-full tw-cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        {portal
          ? (tooltipNode && createPortal(
            <div className="tw-wrapper">{tooltipNode}</div>,
            document.body
          ))
          : tooltipNode}
      </div>
    </div>
  );
};

export default TooltipMC;
